import React, { useState, useRef, useEffect, useCallback } from 'react';
import { ReactSVG } from 'react-svg';
import { getSvgPaths } from '@/utils/svgAssets';
import './choice-card.css';

const ChoiceCard = ({ 
  choice, 
  isSelected, 
  onSelect,
  onHelpClick,
  reduced = false,
  hasSelectionInGroup = false
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const [hasLongContent, setHasLongContent] = useState(false);
  const [isTouchDevice, setIsTouchDevice] = useState(false);
  const [iconPaths, setIconPaths] = useState({ normal: null, hover: null });
  const detailsRef = useRef(null);
  const cardRef = useRef(null);
  
  const updateIconPaths = useCallback(() => {
    if (choice.img) {
      const paths = getSvgPaths(choice.img);
      setIconPaths(paths);
    }
  }, [choice.img]);
  
  useEffect(() => {
    updateIconPaths();
  }, [updateIconPaths]);

  useEffect(() => {
    const handleSelectionChange = (event) => {
    
      if (event.detail && event.detail.choice && event.detail.choice.group === 'Longeur') {
      
        setTimeout(updateIconPaths, 50);
      }
    };
    
    const handleIconRefresh = () => {
      updateIconPaths();
    };
    
    document.addEventListener('selection-changed', handleSelectionChange);
    document.addEventListener('hair-length-icons-refresh', handleIconRefresh);
    
    return () => {
      document.removeEventListener('selection-changed', handleSelectionChange);
      document.removeEventListener('hair-length-icons-refresh', handleIconRefresh);
    };
  }, [updateIconPaths]);
  
  useEffect(() => {
    const detectTouchDevice = () => {
      setIsTouchDevice(('ontouchstart' in window) || 
                       (navigator.maxTouchPoints > 0) || 
                       (navigator.msMaxTouchPoints > 0));
    };
    
    detectTouchDevice();
    
    const handleTouchStart = () => setIsTouchDevice(true);
    window.addEventListener('touchstart', handleTouchStart, { once: true });
    
    return () => {
      window.removeEventListener('touchstart', handleTouchStart);
    };
  }, []);
  
  useEffect(() => {
    if (detailsRef.current) {
      const isContentTall = detailsRef.current.scrollHeight > 48;
      setHasLongContent(isContentTall);
    }
  }, [choice.label, choice.sublabel]);

  useEffect(() => {
    if (!isSelected) {
      setIsHovered(false);
      if (cardRef.current) {
        cardRef.current.classList.remove('hovered');
      }
    }
  }, [isSelected]);

  useEffect(() => {
    const handleGlobalClick = (e) => {
      if (cardRef.current && !cardRef.current.contains(e.target) && !isSelected) {
        setIsHovered(false);
        if (cardRef.current) {
          cardRef.current.classList.remove('hovered');
        }
      }
    };
    
    if (isTouchDevice) {
      document.addEventListener('touchstart', handleGlobalClick);
    } else {
      document.addEventListener('click', handleGlobalClick);
    }
    
    return () => {
      if (isTouchDevice) {
        document.removeEventListener('touchstart', handleGlobalClick);
      } else {
        document.removeEventListener('click', handleGlobalClick);
      }
    };
  }, [isSelected, isTouchDevice]);

  useEffect(() => {
    const handleFrequencySelectorChange = () => {
      if (!isSelected) {
        setIsHovered(false);
        if (cardRef.current) {
          cardRef.current.classList.remove('hovered');
        }
      }
    };
    
    document.addEventListener('change', handleFrequencySelectorChange);
    document.addEventListener('frequency-select-change', handleFrequencySelectorChange);
    
    return () => {
      document.removeEventListener('change', handleFrequencySelectorChange);
      document.removeEventListener('frequency-select-change', handleFrequencySelectorChange);
    };
  }, [isSelected]);

  const isGreyedOut = hasSelectionInGroup && !isSelected;
  
  const cardClasses = [
    'choice-card',
    isSelected ? 'selected' : '',
    (!isSelected && isHovered && !isTouchDevice) ? 'hovered' : '',
    reduced ? 'reduced' : '',
  ].filter(Boolean).join(' ');
  
  const detailsClassName = `choice-details ${hasLongContent ? 'long-content' : ''}`;
  
  const handleMouseEnter = () => {
    if (!isTouchDevice) {
      setIsHovered(true);
    }
  };
  
  const handleMouseLeave = () => {
    if (!isTouchDevice) {
      setIsHovered(false);
    }
  };
  
  const handleCardClick = (e) => {
    if (isTouchDevice) {
      const allCards = document.querySelectorAll('.choice-card:not(.selected)');
      allCards.forEach(card => {
        if (card !== cardRef.current) {
          card.classList.remove('hovered');
        }
      });
    }
    
    if (onSelect) {
      onSelect(e);
    }
  };
  
  return (
    <div 
      ref={cardRef}
      className={cardClasses}
      onClick={handleCardClick}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onBlur={() => setIsHovered(false)}
      data-selected={isSelected}
      data-group={choice.group || 'default'}
      data-greyed={isGreyedOut}
      data-touch-device={isTouchDevice}
    >
      <div className="choice-image-container">
        {choice.img && iconPaths.normal && (
          <div className="icon-container">
            {/* Version normale du SVG */}
            <ReactSVG 
              src={iconPaths.normal} 
              className="icon icon-normal" 
              beforeInjection={(svg) => {
                svg.setAttribute('width', '100%');
                svg.setAttribute('height', '100%');
                svg.setAttribute('preserveAspectRatio', 'xMidYMid meet');
                svg.setAttribute('viewBox', svg.getAttribute('viewBox') || '0 0 100 100');

                const g = document.createElementNS('http://www.w3.org/2000/svg', 'g');
                g.setAttribute('transform', reduced ? 'scale(0.85)' : 'scale(1.0)');
                g.setAttribute('transform-origin', 'center');
                
                while (svg.firstChild) {
                  g.appendChild(svg.firstChild);
                }
                
                svg.appendChild(g);
              }}
              wrapper="span"
              loading={() => (
                <span className="svg-loading"></span>
              )}
              fallback={() => (
                <span className="svg-fallback"></span>
              )}
            />
            
            {/* Version hover/active du SVG */}
            {iconPaths.hover && (
              <ReactSVG 
                src={iconPaths.hover} 
                className="icon icon-active" 
                beforeInjection={(svg) => {
                  svg.setAttribute('width', '100%');
                  svg.setAttribute('height', '100%');
                  svg.setAttribute('preserveAspectRatio', 'xMidYMid meet');
                  svg.setAttribute('viewBox', svg.getAttribute('viewBox') || '0 0 100 100');

                  const g = document.createElementNS('http://www.w3.org/2000/svg', 'g');
                  g.setAttribute('transform', reduced ? 'scale(0.90)' : 'scale(1.0)');
                  g.setAttribute('transform-origin', 'center');
                  
                  while (svg.firstChild) {
                    g.appendChild(svg.firstChild);
                  }
                  
                  svg.appendChild(g);
                }}
                wrapper="span"
                loading={() => (
                  <span className="svg-loading"></span>
                )}
                fallback={() => (
                  <span className="svg-fallback"></span>
                )}
              />
            )}
          </div>
        )}
        
        {choice.help && (
          <div 
            className="help-tooltip"
            onClick={(e) => {
              e.stopPropagation();
              if (onHelpClick) {
                onHelpClick(choice.label, choice.help);
              }
            }}
          >
            <span className="help-icon">?</span>
          </div>
        )}
      </div>
      
      <div className={detailsClassName} ref={detailsRef}>
        <h3 className="choice-label">{choice.label}</h3>
        {choice.sublabel && <p className="choice-sublabel">{choice.sublabel}</p>}
      </div>
    </div>
  );
};

export default ChoiceCard;